import { useEffect } from 'react';
import {
  Device,
  toast,
  QueryParam,
  LocalStorage,
} from '@cashfree-intl/coherent';
import capitalize from 'lodash/capitalize';

import moment from 'moment';
import Bowser from 'bowser';
import { isEmpty, startCase, toLower } from 'lodash';
import getSymbolFromCurrency from 'currency-symbol-map';

import { MERCHANT_CHECKOUT_ONE_REQUEST } from 'containers/CheckoutOneCustomisation/constants';
import { INVALIDATE_TOKEN_URL } from 'containers/TncContainer/constants';
import { REGEXES } from 'constants/regexes';
import {
  META_DATA_MAPPER,
  PAYMENTS_FIELD_MAPPER,
  AMOUNT_TYPE_TIMELINE_FIELDS,
  ACCEPTED_SIGNATURE,
  HOUR_FACTOR,
  FILTERS_BY_NAVIGATION,
  LOGO_MAPPING,
  ICICIMERCHANT,
  LENDINGKARTMERCHANT,
  YESMERCHANT,
  MERCHANT_TEST,
  GLOBAL_SIDEBAR_PRODUCT_ITEM_CONFIGURATION,
  APPROVED,
  DEFAULT_DATE_TIME_PAYLOAD_FORMAT,
  ALL,
  IDFCMERCHANT,
  WHITELABELEDMERCHANT,
  ANNOUNCEMENT_STRIP_DATA,
  SUMMARY,
  UTM_CAMPAIGN_URL,
  UTM_PARAMS,
  CURRENCY_SYMBOL,
  FAQ_CAMPAIGNS_CHECKLIST_NAME_MAPPING,
  MERCHANT_TRANSACTION_DONE,
  USER_PERSONA,
  DEFAULT_SECURITY_HEADERS,
  PROD,
  TEST,
  UNDERSCORE_REQUIRED,
  FILTER_MAPPER,
  PAGE_FILTERS,
  AXISEASYPAYPLUS,
  INSIGHTS_AI_ALERT,
  INSIGHTS_AI_BANNER,
  INSIGHTS_AI_MODAL,
  MAX_PHONE_NUMBER_LENGTH,
  FLOWWISE_CONFIGURATION_V2_START_DATE,
  INSIGHTS_AI_COACHMARK_CLOSE_COUNT,
  SR_CONSTANTS,
  PAYMENT_MODE_FORMATTED_TEXT,
  CUSTOM_BASE_PATH_TYPES,
  VIEWED_REFLECT_2025,
  ANDROID,
  IOS,
  DEVICE_TYPES,
  LAST_1_HOUR,
  LAST_3_HOUR,
  LAST_6_HOUR,
  TODAY,
  YESTERDAY,
  THIS_WEEK,
  LAST_WEEK,
  THIS_MONTH,
  LAST_MONTH,
  CSBMERCHANT,
  IDBIMERCHANT,
  OLD_LOGO_URL,
  SUBS_POPUP_DISMISSED_KEY,
} from './constants';
import { capitalizeFirst } from './capitalizeFirst';

export const setItemsInLocalStorage = obj =>
  Object.keys(obj).forEach(elem => localStorage.setItem(elem, obj[elem]));

export const getItemFromLocalStorage = key => localStorage.getItem(key);

export const removeItemFromLocalStorage = key => localStorage.removeItem(key);

export const getViewport = () => {
  let viewPortWidth;
  let viewPortHeight;

  // the more standards compliant browsers (mozilla/netscape/opera/IE7) use window.innerWidth and window.innerHeight
  if (typeof window.innerWidth !== 'undefined') {
    viewPortWidth = window.innerWidth;
    viewPortHeight = window.innerHeight;
  }

  // IE6 in standards compliant mode (i.e. with a valid doctype as the first line in the document)
  else if (
    typeof document.documentElement !== 'undefined' &&
    typeof document.documentElement.clientWidth !== 'undefined' &&
    document.documentElement.clientWidth !== 0
  ) {
    viewPortWidth = document.documentElement.clientWidth;
    viewPortHeight = document.documentElement.clientHeight;
  }

  // older versions of IE
  else {
    viewPortWidth = document.getElementsByTagName('body')[0].clientWidth;
    viewPortHeight = document.getElementsByTagName('body')[0].clientHeight;
  }
  return [viewPortWidth, viewPortHeight];
};

export const checkIfFormIsComplete = formValues =>
  Object.keys(formValues).every(key => !!formValues[key]);

export const formatDate = (date, formatType) => {
  // 2020-04-02T18:29:12
  const formattedDate = date.split('T');
  if (formatType === 'DOT_SEPARATED')
    return formattedDate[0].split('-').join('.');
  const dateObj = new Date(formattedDate[0]);
  const dateVal = dateObj.getDate();
  const monthVal = dateObj.toUTCString().substr(8, 3);
  return `${dateVal} ${monthVal}`;
};

export const formatTime = time => {
  const formattedTime = time.split(':');
  const isAM = formattedTime[0] <= 12;
  const suffix = isAM ? 'am' : 'pm';
  let hours = isAM ? formattedTime[0] : parseInt(formattedTime[0], 10) - 12;
  const minutes = formattedTime[1];
  if (hours < 10) hours = `0${hours}`;
  return `${hours}:${minutes} ${suffix}`;
};

export const initRedirectionForm = (
  redirectToTest,
  dashboardType,
  options = {},
) => {
  const form = document.createElement('form');
  const token = getItemFromLocalStorage('merchantToken');

  const legacyUrl = getLegacyAppUrl();

  let formFields = [];

  if (redirectToTest)
    formFields = formFields.concat({ name: 'goToTest', value: 1 });

  const { target, iframe } = options;

  if (iframe) formFields = formFields.concat({ name: 'iframe', value: iframe });

  formFields = formFields.concat([
    { name: 'token', value: token },
    { name: 'merchantToken', value: token },
  ]);

  if (dashboardType)
    formFields.push({ name: 'dashboardType', value: dashboardType });

  form.setAttribute('method', 'post');
  form.setAttribute('action', `${legacyUrl}/jwt-auth-v2`);

  if (target) form.setAttribute('target', target);

  formFields.forEach(field => {
    const input = document.createElement('input');

    input.setAttribute('type', 'hidden');
    input.setAttribute('name', field.name);
    input.setAttribute('value', field.value);

    form.appendChild(input);
  });

  document.getElementById('jwt-auth-form').appendChild(form);

  if (token) form.submit();

  document.getElementById('jwt-auth-form').removeChild(form);
};

export const formatAmount = amount => {
  if (amount < 4) {
    return amount;
  }
  const decimalBreak = amount.split('.');
  const beforeDecimal = decimalBreak[0];
  const afterDecimal = decimalBreak[1] || '';
  const lastThree = beforeDecimal.substring(beforeDecimal.length - 3);
  const otherNumbers = beforeDecimal.substring(0, beforeDecimal.length - 3);
  let formattedAmount = '';

  let i = 0;
  while (i < otherNumbers.length) {
    if (
      otherNumbers.slice(otherNumbers.length - i, otherNumbers.length - i + 2)
    )
      formattedAmount += `,${otherNumbers.slice(
        otherNumbers.length - i,
        otherNumbers.length - i + 2,
      )}`;
    i += 2;
  }
  formattedAmount = afterDecimal
    ? `₹ ${formattedAmount.slice(1)},${lastThree}.${afterDecimal}`
    : `₹ ${formattedAmount.slice(1)},${lastThree}`;
  return formattedAmount;
};

export const getRemarksConclusion = remark =>
  remark.indexOf('Higher') === 0 ? 'HIGHER' : 'LOWER';

export const preventPasteEvent = e => {
  e.preventDefault();
  return false;
};

export const filterAllowedValues = ({ allowedValuesType, asciiValue }) => {
  switch (allowedValuesType) {
    case 'alphabetsWithSpaces': {
      return (
        (asciiValue >= 65 && asciiValue <= 90) ||
        (asciiValue >= 97 && asciiValue <= 122) ||
        asciiValue === 32
      );
    }
    case 'alphabetsOnly': {
      return (
        (asciiValue >= 65 && asciiValue <= 90) ||
        (asciiValue >= 97 && asciiValue <= 122)
      );
    }
    case 'alphabetsWithSpacesAndDot': {
      return (
        (asciiValue >= 65 && asciiValue <= 90) ||
        (asciiValue >= 97 && asciiValue <= 122) ||
        asciiValue === 32 ||
        asciiValue === 46
      );
    }
    case 'alphanumeric': {
      return (
        (asciiValue >= 65 && asciiValue <= 90) ||
        (asciiValue >= 97 && asciiValue <= 122) ||
        (asciiValue >= 48 && asciiValue <= 57)
      );
    }
    case 'alphanumericWithSpaces': {
      return (
        (asciiValue >= 65 && asciiValue <= 90) ||
        (asciiValue >= 97 && asciiValue <= 122) ||
        (asciiValue >= 48 && asciiValue <= 57) ||
        asciiValue === 32
      );
    }
    case 'alphanumericWithHyphen': {
      return (
        (asciiValue >= 65 && asciiValue <= 90) ||
        (asciiValue >= 97 && asciiValue <= 122) ||
        (asciiValue >= 48 && asciiValue <= 57) ||
        asciiValue === 45
      );
    }
    case 'numeric':
      return asciiValue >= 48 && asciiValue <= 57;
    case 'numericWithDots':
      return (asciiValue >= 48 && asciiValue <= 57) || asciiValue === 46;
    case 'wordWithoutCommaAndBracket':
      return ![40, 41, 44].includes(asciiValue);
    default:
      return true;
  }
};

export const convertUrlToObject = (url = '') => {
  const arr = url.slice(1).split(/&|=/);
  const params = {};

  for (let i = 0; i < arr.length; i += 2) {
    const key = arr[i];
    const value = arr[i + 1];
    if (key && value) params[key] = value;
  }
  return params;
};

export const getModeByPaymentCode = paymentCode => {
  if (paymentCode >= 3000 && paymentCode <= 3999) return 'NET_BANKING';
  if ((paymentCode >= 7000 && paymentCode <= 7999) || paymentCode === 130001)
    return 'UPI';
  if (
    (paymentCode >= 4000 && paymentCode <= 4999) ||
    paymentCode === 6002 ||
    paymentCode === 6003 ||
    paymentCode === 5001 ||
    paymentCode === 8001 ||
    paymentCode === 12001
  )
    return 'NON_CARD_BASED_PAYMENTS';
  return 'DEFAULT';
};

export const getMetaDataMapperByKey = (key, dataSourceObj) => {
  const paymentMode = dataSourceObj.paymentCode
    ? getModeByPaymentCode(dataSourceObj.paymentCode)
    : null;
  if (!key || !META_DATA_MAPPER[key]) return {};
  if (key !== 'PAYMENT_CREATED') return META_DATA_MAPPER[key];
  return {
    ...META_DATA_MAPPER[key],
    ...PAYMENTS_FIELD_MAPPER[paymentMode],
  };
};

const removeUnderscoreOnCondition = (fieldName, dataSourceObj) =>
  UNDERSCORE_REQUIRED.includes(fieldName)
    ? dataSourceObj[fieldName]
    : removeCharacter(dataSourceObj[fieldName], '_', ' ') || '';

export const getFormattedMetaData = (key, dataSourceObj, currency = 'INR') => {
  const currencySymbol = getCurrencySymbol(currency);
  const metaDataMapper = getMetaDataMapperByKey(key, dataSourceObj);

  if (!key || !META_DATA_MAPPER[key]) return [];
  return Object.keys(metaDataMapper).map(fieldName => {
    const symbol =
      !currencySymbol || currencySymbol === '' ? currency : currencySymbol;
    return {
      fieldName: metaDataMapper[fieldName],
      fieldValue:
        AMOUNT_TYPE_TIMELINE_FIELDS.includes(fieldName) &&
        dataSourceObj[fieldName] !== 'N/A'
          ? `${symbol} ${dataSourceObj[fieldName] || 0}`
          : removeUnderscoreOnCondition(fieldName, dataSourceObj),
    };
  });
};

export const getOrderAmountOptions = (obj, key = 'orderAmount') => {
  const options = {};
  const { startOrderAmount, endOrderAmount } = obj;

  if (obj[key] === 'IS_LESS_THAN_EQUAL_TO')
    options.endOrderAmount = startOrderAmount;
  else if (obj[key] === 'IS_EQUAL_TO') {
    options.startOrderAmount = startOrderAmount;
    options.endOrderAmount = startOrderAmount;
  } else if (obj[key] === 'IS_GREATER_THAN_EQUAL_TO') {
    options.startOrderAmount = startOrderAmount;
  } else {
    options.startOrderAmount = startOrderAmount;
    options.endOrderAmount = endOrderAmount;
  }
  return options;
};

export const getGroupingFactor = diff => {
  let factor = '';
  if (diff <= 7) factor = 1;
  else if (diff > 7 && diff <= 15) factor = 2;
  else if (diff > 15 && diff <= 21) factor = 3;
  else factor = 4;
  return factor;
};

export const extractDateDataFromResponse = (
  startRange,
  endRange,
  response,
  dataKey = 'amount',
) => {
  let totalAmount = 0;
  let totalCount = 0;
  let dataFound = false;
  Object.keys(response).forEach(key => {
    if (
      endRange.diff(moment(key, 'YYYY-MM-DD')) >= 0 &&
      moment(key, 'YYYY-MM-DD').diff(startRange) >= 0
    ) {
      if (!dataFound) dataFound = true;
      totalAmount += response[key][dataKey] ? +response[key][dataKey] : 0;
      totalCount += response[key].count ? +response[key].count : 0;
    }
  });
  return { totalAmount, totalCount, dataFound };
};

export const formatHourReponseForComputation = data => {
  const res = {};
  data.forEach(obj => {
    const val = obj.hour && obj.hour.split(' ')[0];
    res[moment(val, 'HH').format('YYYY-MM-DD HH:mm:ss')] = obj;
  });
  return res;
};

export const formatDateResponseForComputation = data => {
  const res = {};
  data.forEach(obj => {
    res[obj.date] = obj;
  });
  return res;
};

export const extractHourDataFromResponse = (
  startRange,
  endRange,
  response,
  dataKey = 'amount',
) => {
  let totalAmount = 0;
  let totalCount = 0;
  let dataFound = false;
  Object.keys(response).forEach(key => {
    if (endRange.diff(moment(key)) >= 0 && moment(key).diff(startRange) >= 0) {
      if (!dataFound) dataFound = true;
      totalAmount += response[key][dataKey] ? +response[key][dataKey] : 0;
      totalCount += response[key].count ? +response[key].count : 0;
    }
  });
  return { totalAmount, totalCount, dataFound };
};

export const computeHourDataForGraph = (
  startDate,
  endDate,
  response,
  dataKey,
) => {
  const result = [];
  let isDataFound = false;

  const isToday =
    moment(startDate).diff(endDate, 'days') === 0 &&
    moment(startDate).diff(moment(), 'days') === 0;

  const data = formatHourReponseForComputation(response);

  for (let i = 0; i < 24; i += HOUR_FACTOR) {
    let offsetDate = '';
    // calculating the offset date
    if (result.length > 0)
      offsetDate = moment(result[result.length - 1].endRange.clone()).add(
        1,
        'second',
      );
    else
      offsetDate = moment()
        .startOf('day')
        .clone();
    // preparing the resultant obj
    let endRange = moment(offsetDate.clone())
      .add(HOUR_FACTOR, 'hours')
      .subtract(1, 'second');
    // for edge cases where because of adding factor the endRange is greater than the endDate
    if (isToday && moment(endRange).diff(moment()) > 0) endRange = moment();

    const startRange = moment(offsetDate.clone());

    if (startRange.diff(endRange, 'hours') !== 0) {
      const {
        totalAmount,
        totalCount,
        dataFound,
      } = extractHourDataFromResponse(startRange, endRange, data, dataKey);
      if (!isDataFound) isDataFound = dataFound;

      result.push({
        endRange,
        startRange,
        displayText: `${startRange.format('h:mm-')}${endRange.format(
          'h:mm A',
        )}`,
        totalAmount,
        totalCount,
      });
    }
  }
  return { data: result, dataFound: isDataFound };
};

export const computeDateDataForGraph = (
  startDate,
  endDate,
  response,
  dataKey,
) => {
  const diff = moment(endDate, 'MM-DD-YYYY').diff(startDate, 'days');
  const factor = getGroupingFactor(diff);
  const data = formatDateResponseForComputation(response);

  const result = [];
  let isDataFound = false;

  for (let i = 0; i < diff; i += factor) {
    let offsetDate = '';
    // calculating the offset date
    if (result.length > 0)
      offsetDate = moment(result[result.length - 1].endRange.clone()).add(
        1,
        'second',
      );
    else offsetDate = startDate.clone();
    // preparing the resultant obj
    let endRange = moment(offsetDate.clone())
      .add(factor, 'days')
      .subtract(1, 'second');
    // for edge cases where because of adding factor the endRange is greater than the endDate
    if (moment(endRange).diff(endDate) > 0)
      endRange = moment(endDate.clone())
        .add(1, 'days')
        .subtract(1, 'second');

    const startRange = moment(offsetDate.clone());

    const { totalAmount, totalCount, dataFound } = extractDateDataFromResponse(
      startRange,
      endRange,
      data,
      dataKey,
    );

    if (!isDataFound) isDataFound = dataFound;

    const isEndRangeBefore = endRange.isBefore(endDate);

    const displayText = isEndRangeBefore
      ? endRange.format('DD MMM')
      : endDate.format('DD MMM');

    result.push({
      endRange: isEndRangeBefore ? endRange : endDate,
      startRange,
      displayText:
        factor === 1
          ? endRange.format('DD MMM')
          : `${startRange.format('DD-')}${displayText}`,
      totalAmount,
      totalCount,
    });
  }
  // for edge cases where some date ranges are left out
  if (
    result.length > 0 &&
    moment(result[result.length - 1].endRange.clone()).diff(endDate) < 0
  ) {
    const startRange = moment(result[result.length - 1].endRange.clone()).add(
      1,
      'second',
    );

    const endRange = moment(startRange.clone())
      .add(1, 'days')
      .subtract(1, 'second');

    const { totalAmount, totalCount, dataFound } = extractDateDataFromResponse(
      startRange,
      endRange,
      data,
      dataKey,
    );

    if (!isDataFound) isDataFound = dataFound;

    result.push({
      endRange,
      startRange,
      displayText:
        factor === 1 ||
        startRange.format('DD MMM') === endRange.format('DD MMM')
          ? endRange.format('DD MMM')
          : `${startRange.format('DD-')}${endRange.format('DD MMM')}`,
      totalAmount,
      totalCount,
    });
  }
  return { data: result, dataFound: isDataFound };
};

export const formatDataForSummaryPage = (
  appliedFilters = {},
  response = [],
  dataKey = 'amount',
) => {
  const startDate = moment(appliedFilters.startDate, 'YYYY-MM-DD HH:mm:ss');
  const endDate = moment(appliedFilters.endDate, 'YYYY-MM-DD HH:mm:ss');
  const isDiffHours =
    (response.length > 0 && response[0].hour) ||
    startDate.diff(endDate, 'days') === 0;

  if (isDiffHours)
    return computeHourDataForGraph(startDate, endDate, response, dataKey);
  return computeDateDataForGraph(startDate, endDate, response, dataKey);
};

export const useQuery = () =>
  new URLSearchParams(new URL(window.location).search);

export const useClickOutside = (ref, callbackFn, ...extraRef) => {
  const checkForOutsideClick = event => {
    let isClickedOutside = true;

    if (ref.current && ref.current.contains(event.target)) {
      isClickedOutside = false;
    }

    extraRef.forEach(reference => {
      if (reference.current && reference.current.contains(event.target)) {
        isClickedOutside = false;
      }
    });
    return isClickedOutside;
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (checkForOutsideClick(event)) {
        callbackFn();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, callbackFn]);
};

export const copyToClipboard = (value, showToast = true) => e => {
  if (navigator && navigator.clipboard) {
    navigator.clipboard.writeText(value);
  }

  if (showToast) {
    toast.success('Copied to Clipboard', {
      className: 'toast-class',
      bodyClassName: 'toast-body',
      position: 'bottom-center',
      hideProgressBar: true,
    });
  }

  if (e) e.stopPropagation();
};

/* eslint-disable consistent-return */
export const exportToCSV = (
  filename,
  rows,
  isColumn = false,
  fileRequired = false,
  excludeIndexHeaders = false, // New prop to remove numeric headers
) => {
  const processRow = oRow => {
    let finalVal = '';
    let row;
    if (typeof oRow === 'object') {
      row = Object.values(oRow);
    } else {
      row = oRow;
    }
    for (let j = 0; j < row.length; j += 1) {
      let innerValue = row[j] === null ? '' : row[j].toString();
      if (row[j] instanceof Date) {
        innerValue = row[j].toLocaleString();
      }
      let result = innerValue.replace(/"/g, '""');
      if (result.search(/("|,|\n)/g) >= 0) {
        result = `"${result}"`;
      }
      if (j > 0) {
        finalVal += ',';
      }
      finalVal += result;
    }
    return `${finalVal}\n`;
  };
  let csvFile = '';
  if (isColumn && rows.length) {
    let headers = Object.keys(rows[0]);

    if (excludeIndexHeaders) {
      // eslint-disable-next-line no-restricted-globals
      headers = headers.filter(key => isNaN(key)); // Remove numeric keys
    }
    csvFile += processRow(headers);
  }
  for (let i = 0; i < rows.length; i += 1) {
    csvFile += processRow(rows[i]);
  }

  if (fileRequired) {
    const file = new File([csvFile], `${filename}.csv`, {
      type: 'text/csv;charset=utf-8;',
    });
    return file;
  }

  const blob = new Blob([csvFile], { type: 'text/csv;charset=utf-8;' });
  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, filename);
  } else {
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

export const getFilterCount = (obj = {}) => {
  let count = 0;
  Object.keys(obj).forEach(key => {
    if (
      Object.keys(FILTER_MAPPER).find(item =>
        [FILTER_MAPPER[item].start, FILTER_MAPPER[item].end].includes(key),
      )
    )
      return;
    if (Array.isArray(obj[key])) {
      count += obj[key].length;
    } else if (
      typeof obj[key] === 'object' &&
      obj[key].value &&
      obj[key].value.trim()
    ) {
      count += 1;
    } else if (Object.keys(FILTER_MAPPER).includes(key)) {
      if (
        obj[key] === 'IS_BETWEEN' &&
        obj[FILTER_MAPPER[key].start] &&
        obj[FILTER_MAPPER[key].end]
      )
        count += 1;
      else if (obj[key] !== 'IS_BETWEEN' && obj[FILTER_MAPPER[key].start])
        count += 1;
    } else if (obj[key] && typeof obj[key] !== 'object') count += 1;
  });
  return count;
};

/**
 * using fetch instead of request.js to avoid the loop of invalidate api call
 * that can occur with below steps:
 * 1. when request() returns UNAUTHORISED, it further triggers logout()
 * 2. logout triggers invalidate token api call using request which further returns UNAUTHORISED
 * and this will take user to step 1 and this loop will continue forever.
 *
 * Hence to avoid this scenarios we have to use explicit fetch.
 */
const invalidateToken = async () => {
  const { url, method } = INVALIDATE_TOKEN_URL;
  const { baseUrl, token } = getRequestUrlAndToken();

  const defaultHeaders = await getRequestHeaders({});
  const headers = { ...defaultHeaders, Authorization: `Bearer ${token}` };

  await fetch(`${baseUrl}${url}`, { headers, method });
};

export const logout = async (isManualLogout = false) => {
  try {
    await invalidateToken();

    if (!isEmpty(localStorage))
      Object.keys(localStorage).forEach(item => {
        if (
          ![
            'deviceId',
            MERCHANT_TRANSACTION_DONE,
            USER_PERSONA,
            PAGE_FILTERS,
            MERCHANT_CHECKOUT_ONE_REQUEST,
            INSIGHTS_AI_BANNER,
            INSIGHTS_AI_ALERT,
            INSIGHTS_AI_MODAL,
            INSIGHTS_AI_COACHMARK_CLOSE_COUNT,
            VIEWED_REFLECT_2025,
            SUBS_POPUP_DISMISSED_KEY,
          ].includes(item)
        )
          removeItemFromLocalStorage(item);
      });

    if (Object.prototype.hasOwnProperty.call(window, 'Moengage')) {
      // eslint-disable-next-line no-unused-expressions, no-undef
      Moengage?.destroy_session();
    }

    // Only preserve redirectSsoUrl for automatic token expiration, not manual logout
    if (isManualLogout) {
      // Clear all session storage for manual logout
      sessionStorage.clear();
    } else {
      // Preserve redirectSsoUrl during logout for automatic token expiration scenarios
      const redirectSsoUrl = sessionStorage.getItem('redirectSsoUrl');
      const referrerUrl = sessionStorage.getItem('referrerUrl');

      // Clear all session storage
      sessionStorage.clear();

      // Restore redirectSsoUrl if it existed
      if (redirectSsoUrl) {
        sessionStorage.setItem('redirectSsoUrl', redirectSsoUrl);
      }
      if (referrerUrl) {
        sessionStorage.setItem('referrerUrl', referrerUrl);
      }
    }

    const legacyUrl = getLegacyAppUrl();

    window.location.href = legacyUrl;

    if (window.IAccountActivationJavaScript) {
      window.IAccountActivationJavaScript.logout();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
};

export const numberWithCommas = (x = '') =>
  x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const indianCurrencyWithCommas = val => {
  const x = val.toString();
  const significantNumber = x.split('.')[0];
  const decimals = x.split('.')[1];
  let lastThree = significantNumber.substring(significantNumber.length - 3);
  const otherNumbers = significantNumber.substring(
    0,
    significantNumber.length - 3,
  );
  if (otherNumbers !== '') lastThree = `,${lastThree}`;
  const result = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree;
  if (decimals) {
    return `${result}.${decimals}`;
  }
  return result;
};

const checkIfDecimalNumber = x => String(x).split('.').length > 1;

export function roundTo(n, digits) {
  let num = n;
  let noOfDecimals = digits;
  if (digits === undefined) {
    noOfDecimals = 0;
  }

  // eslint-disable-next-line no-restricted-properties
  const multiplicator = Math.pow(10, digits);
  num = parseFloat((n * multiplicator).toFixed(11));
  const test = Math.round(num) / multiplicator;
  return test.toFixed(noOfDecimals);
}

export const roundOffNumber = num => {
  if (!num) return num;
  return roundTo(num, 2);
};

const prepareAbbreviation = (amount, suffix) => {
  const isDecimalNumber = checkIfDecimalNumber(amount);
  return `${String(amount).substring(0, isDecimalNumber ? 4 : 3)}${suffix}`;
};

export const convertAmountToAbbreviation = (
  amount = '',
  convertForThousands = false,
) => {
  const countSignificantDigits = String(amount).split('.')[0].length;

  if (convertForThousands && [4, 5].includes(countSignificantDigits)) {
    const result = amount / 1000;
    return prepareAbbreviation(result, 'K');
  }

  if ([6, 7].includes(countSignificantDigits)) {
    const result = amount / 100000;
    return prepareAbbreviation(result, 'L');
  }

  if ([8, 9].includes(countSignificantDigits)) {
    const result = amount / 10000000;

    return prepareAbbreviation(result, 'Cr');
  }

  if (countSignificantDigits > 9) {
    const result = amount / 10000000;
    return `${
      checkIfDecimalNumber(result) ? roundOffNumber(result) : result
    }Cr`;
  }
  return numberWithCommas(
    checkIfDecimalNumber(amount) ? roundOffNumber(amount) : amount,
  );
};

export const formatFileValidationErrorMessage = signatures => {
  let message = '';
  const { length } = Object.keys(signatures);

  Object.keys(signatures).forEach((key, index) => {
    if (length === 1) message = key;
    else if (index === length - 1) message += `and ${key}`;
    else if (index === length - 2) message += `${key} `;
    else message += `${key}, `;
  });

  return message;
};

export const validateFileType = (file, options = {}) =>
  new Promise((resolve, reject) => {
    const filereader = new FileReader();
    const signatures = options.signatures || ACCEPTED_SIGNATURE;
    // eslint-disable-next-line consistent-return
    filereader.onloadend = evt => {
      if (evt.target.readyState === FileReader.DONE) {
        const uint = new Uint8Array(evt.target.result);
        const bytes = [];
        uint.forEach(byte => {
          bytes.push(byte.toString(16));
        });
        const hex = bytes.join('').toUpperCase();
        const validSignature = Object.keys(signatures).filter(
          val =>
            hex === signatures[val] || hex.substring(0, 6) === signatures[val],
        );

        // eslint-disable-next-line consistent-return
        validSignature.forEach(signature => {
          const regex = new RegExp(`\\.${signature}$`);
          if (signatures[signature] && regex.test(file.name)) {
            return resolve(signature);
          }
        });

        return reject(
          // eslint-disable-next-line
          new Error(
            `Error while uploading file. Only files with the following formats are supported: ${formatFileValidationErrorMessage(
              signatures,
            )}`,
          ),
        );
      }
    };
    const blob = file.slice(0, 4);
    filereader.readAsArrayBuffer(blob);
  });

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // eslint-disable-next-line no-restricted-properties
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const downloadFromUrl = (url, fileName, openInNewTab = false) => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${fileName}`);
  if (openInNewTab) link.setAttribute('target', '_blank');
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
};

export const previewFile = url => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('target', '_blank');
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
};

export const getCookie = cookieName => {
  const name = `${cookieName}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i += 1) {
    let c = ca[i];
    while (typeof c === 'string' && c.length > 0 && c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
};

export const setCookie = (cookieName, cookieValue, maxAge) => {
  const expires = `; max-age=${maxAge || 3600}`; // default to 1 hour

  document.cookie = `${cookieName}=${cookieValue ||
    ''}${expires}; path=/; domain=.cashfree.com;`;
};

export const getParam = (url, param) => {
  const match = RegExp(`[?&]${param}=([^&]*)`).exec(url);
  return match && decodeURIComponent(match[1].replace(/\+/g, ' '));
};

export const formatStringForDisplay = (str, separator = '_') => {
  if (!str) return;
  const arr = str.split(separator);
  if (arr.length > 1) {
    // eslint-disable-next-line consistent-return
    return arr
      .map(word => {
        const updatedWord = word.toLowerCase();
        return `${updatedWord[0]?.toUpperCase() || ''}${updatedWord?.substring(
          1,
          updatedWord?.length,
        )}`;
      })
      .join(' ');
  }
  // eslint-disable-next-line consistent-return
  return capitalize(str);
};

export const isColor = strColor => {
  const s = new Option().style;
  s.color = strColor;
  return s.color !== '';
};

export const removeCharacter = (str, inputChar, outChar = '') => {
  if (!str) return str;
  return String(str).replace(new RegExp(inputChar, 'g'), outChar);
};

export const prepareFilterObject = (obj, activeNavigation) => {
  const { FILTER_ITEMS } = FILTERS_BY_NAVIGATION[activeNavigation];
  const resultant = {};
  Object.keys(obj).forEach(key => {
    const lookupKey = key.toUpperCase();
    const mapperObj = FILTER_ITEMS[lookupKey];
    if (mapperObj && mapperObj.items && mapperObj.items[obj[key]]) {
      if (!resultant[lookupKey]) resultant[lookupKey] = {};
      resultant[lookupKey] = { ...resultant[lookupKey], [obj[key]]: true };
    }
  });
  return resultant;
};

export const getReferrerFromURL = () => {
  const { hostname } = window.location;
  return hostname.split('.')[0];
};

export const isReferralFlow = referrer => {
  const { MERCHANT, PROD: prod, ...rest } = LOGO_MAPPING;
  return Object.keys(rest).includes(referrer.toUpperCase());
};

export const getLogoByReferrer = (_ref = '', logoTheme = 'light') => {
  const referrer = getReferrerFromURL();
  let updatedReferrer = '';

  if (_ref && isReferralFlow(_ref)) updatedReferrer = _ref;
  else if (isReferralFlow(referrer)) updatedReferrer = referrer;

  const referrerKey = updatedReferrer.toUpperCase();

  if (referrerKey === 'CSBMERCHANT' || referrerKey === 'CSB') {
    const csbLogos = {
      light: `${OLD_LOGO_URL}/CF_CSB_LOGO.png`,
      dark: `${OLD_LOGO_URL}/Cashfree_CDB_Logo.png`,
    };
    return csbLogos[logoTheme] || csbLogos.light;
  }

  if (referrerKey === 'IDBIMERCHANT' || referrerKey === 'IDBI') {
    const idbiLogos = {
      light: `${OLD_LOGO_URL}/cf_idbi_merchant_logo.png`,
      dark: `${OLD_LOGO_URL}/Cashfree_Idbi_Logo.png`,
    };
    return idbiLogos[logoTheme] || idbiLogos.light;
  }

  const logoMapping = LOGO_MAPPING[referrerKey];

  return logoMapping || LOGO_MAPPING.MERCHANT;
};

export const getLegacyAppUrl = () => {
  let referrer = getReferrerFromURL();
  referrer = referrer && referrer.toUpperCase();
  if (referrer === ICICIMERCHANT) return process.env.ICICI_LEGACY_APP;
  if (referrer === YESMERCHANT) return process.env.YES_LEGACY_APP;
  if (referrer === IDFCMERCHANT) return process.env.IDFC_LEGACY_APP;
  if (referrer === CSBMERCHANT) return process.env.CSB_LEGACY_APP;
  if (referrer === IDBIMERCHANT) return process.env.IDBI_LEGACY_APP;
  if (referrer === LENDINGKARTMERCHANT)
    return process.env.LENDING_KART_LEGACY_APP;
  if (referrer === AXISEASYPAYPLUS) return process.env.AXIS_DASHBOARD_URL;
  return process.env.LEGACY_APP;
};

export const getElementSize = (elem, divisor) => {
  let h = 0;
  let w = 0;
  try {
    if (elem && elem.clientHeight && elem.clientWidth) {
      h = Math.round(elem.clientHeight / divisor);
      w = Math.round(elem.clientWidth / divisor);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
  }
  return {
    height: h,
    width: w,
  };
};

export const isICICIDashboard = () =>
  [ICICIMERCHANT, MERCHANT_TEST].includes(getReferrerFromURL().toUpperCase());

export const isAxisDashboard = () =>
  [AXISEASYPAYPLUS].includes(getReferrerFromURL().toUpperCase());

export const isIDFCDashboard = () =>
  [IDFCMERCHANT, WHITELABELEDMERCHANT].includes(
    getReferrerFromURL().toUpperCase(),
  );

export const isCSBDashboard = () =>
  [CSBMERCHANT].includes(getReferrerFromURL().toUpperCase());

export const isIDBIDashboard = () =>
  [IDBIMERCHANT].includes(getReferrerFromURL().toUpperCase());

export const isLKDashboard = () =>
  [LENDINGKARTMERCHANT].includes(getReferrerFromURL().toUpperCase());

export const isYesMerchant = () =>
  [YESMERCHANT].includes(getReferrerFromURL().toUpperCase());

export const titleCase = (str = '') => {
  if (typeof str !== 'string' || !str) {
    return '';
  }
  const exceptions = new Set(['UPI', 'HDFC', 'SBI']); // add strings that should not be converted to title case
  return str
    .split('_')
    .map(word =>
      exceptions.has(word) ? word : capitalizeFirst(word.toLowerCase()),
    )
    .join(' ');
};

export const convertToTitleCase = (str = '') => {
  if (typeof str !== 'string' || str.trim() === '') {
    return '';
  }

  const minorWords = new Set([
    'a',
    'an',
    'and',
    'as',
    'at',
    'but',
    'by',
    'for',
    'in',
    'nor',
    'of',
    'on',
    'or',
    'so',
    'the',
    'to',
    'up',
    'yet',
  ]);

  const words = str.toLowerCase().split(/[_\s]+/);

  const result = words
    .map((word, index) => {
      if (word.length === 0) {
        return '';
      }
      if (index === 0 || !minorWords.has(word)) {
        return capitalizeFirst(word);
      }
      return word;
    })
    .join(' ');
  return result;
};

export const camelCaseToTitleCase = str => {
  // converts 'helloThereMister' to 'Hello There Mister'
  const result = str.replace(/([A-Z])/g, ' $1');
  const finalResult = capitalizeFirst(result);
  return finalResult.trim();
};

export const capitalizeEveryWord = str => {
  const words = str.split(' ');
  const capitalizedWords = words.map(word =>
    word ? capitalizeFirst(word.toLowerCase()) : word,
  );
  return capitalizedWords.join('_');
};

export const prepareFiltersForDisplayObj = (filters, mapper) => {
  const { startDate, endDate, ...rest } = filters;
  const resultant = [];
  // Some containers expose only SEARCH_ITEMS and no FILTER_ITEMS map (e.g. v2
  // PgIncidents). Dereferencing a missing map used to throw here, which killed
  // the caller mid-dispatch — the date filter/search silently stopped applying
  // as soon as appliedFilters held any non-date key (even an emptied one).
  const filterItems = mapper?.FILTER_ITEMS || {};
  const searchItems = mapper?.SEARCH_ITEMS || [];

  Object.keys(rest).forEach(key => {
    if (['startOrderAmount', 'endOrderAmount', 'saved'].includes(key)) return;
    if (Array.isArray(rest[key])) {
      const obj = filterItems[key];
      if (obj && obj.items) {
        rest[key].forEach(elem => {
          if (obj.items[elem]) {
            if (typeof obj.items[elem] === 'object') {
              const subFilter = obj.items[elem];
              if (rest[subFilter.key] && rest[subFilter.key].length) {
                // if 1 then e.g. Net Banking: Punjab National Bank
                // else Net Banking: Punjab National Bank and more filters
                let subFilterVal = Object.keys(subFilter.items).find(k =>
                  rest[subFilter.key].includes(k),
                );
                subFilterVal = subFilter.items[subFilterVal];
                if (rest[subFilter.key].length > 2)
                  subFilterVal += ' and more filters';
                resultant.push({
                  displayText: `${
                    obj.items[elem].displayText
                  }: ${subFilterVal}`,
                  key,
                  value: elem,
                  subFilter: subFilter.key,
                });
              }
            } else {
              resultant.push({
                displayText: obj.items[elem],
                key,
                value: elem,
              });
            }
          }
        });
      }
    } else if (typeof rest[key] === 'object') {
      if (rest[key].value && rest[key].value.trim()) {
        const obj = searchItems.find(({ value }) => value === rest[key].type);
        resultant.push({
          displayText: `${obj?.text || rest[key].type} - ${rest[key].value}`,
          key,
          value: rest[key].value.trim(),
        });
      }
    } else if (key === 'orderAmount') {
      // write the logic here
      const options = getOrderAmountOptions(filters);
      if (Object.keys(options).every(o => options[o])) {
        const text = Object.keys(options)
          .map(o => `₹ ${options[o]}`)
          .join(' - ');
        if (text) {
          const obj = filterItems[key]?.items?.find(
            ({ value }) => value === rest[key],
          );
          resultant.push({
            displayText: `${obj?.text || rest[key]} - ${text}`,
            key,
          });
        }
      }
    } else {
      const obj = filterItems[key]?.items?.find(
        ({ value }) => value === rest[key],
      );
      resultant.push({
        displayText: obj?.text || rest[key],
        key,
        value: rest[key],
      });
    }
  });
  return resultant;
};

export const getActiveNavigation = (hash, navigationOptions = []) => {
  const hashArray = hash.split('#');
  let activeNavigation = navigationOptions[0].text;

  if (hashArray.length > 1) {
    const key = hashArray.pop();

    const result = navigationOptions.find(obj => obj.key === key);
    if (result) activeNavigation = result.text;
  }
  return activeNavigation;
};

export const getActiveNavigationHash = (name, navigationOptions = []) => {
  const option = navigationOptions.find(obj => obj.text === name);
  return option?.key;
};

export const objectToURLSearchParams = obj =>
  new URLSearchParams(obj).toString();

export const setActiveNavigation = (
  name,
  navigateRouter,
  location,
  navigationOptions,
) => {
  if (!location) return;

  const key = getActiveNavigationHash(name, navigationOptions);
  const { pathname, search } = location;

  let searchString = search;
  const qsObj = convertUrlToObject(search);

  if (qsObj.activeRowId) {
    const { activeRowId, ...rest } = qsObj;
    searchString = `?${objectToURLSearchParams(rest)}`;
  }
  // eslint-disable-next-line consistent-return
  return navigateRouter(`${pathname}${searchString}${key ? `#${key}` : ''}`);
};

export const getCustomProductItems = productStatus => {
  const hiddenItems = [];

  if (productStatus.mkpSettlementsStatus !== APPROVED) {
    hiddenItems.push({
      key: 'settlements',
      visible: false,
    });
  }

  if (productStatus.baasStatus !== APPROVED) {
    hiddenItems.push({
      key: 'baas',
      visible: false,
    });

    if (isICICIDashboard() || isIDFCDashboard()) {
      hiddenItems.push(
        {
          key: 'payout',
          visible: false,
        },
        {
          key: 'settlements',
          visible: false,
        },
        {
          key: 'verification-suite',
          visible: false,
        },
      );
    }
  }

  return [...GLOBAL_SIDEBAR_PRODUCT_ITEM_CONFIGURATION, ...hiddenItems];
};

export const isSubscriptionEnabled = () => {
  const subscriptionEnabled = getItemFromLocalStorage('subscriptionEnabled');
  return subscriptionEnabled === 'true';
};

// TODO: TO BE CHANGED AFTER API READY
export const isBBPSEnabled = () => {
  const mid = getItemFromLocalStorage('merchantId');
  return ['486487', '412910'].includes(mid);
};

export const checkIframeLoaded = (id, cb) => {
  try {
    // Get a handle to the iframe element
    const iframe = document.getElementById(id);
    if (iframe) {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

      // Check if loading is complete
      if (iframeDoc.readyState === 'complete') {
        // iframe.contentWindow.alert("Hello");
        // eslint-disable-next-line func-names
        iframe.contentWindow.onload = function() {
          cb();
        };
        // The loading is complete, call the function we want executed once the iframe is loaded
        cb();
        return;
      }
    } else {
      cb();
      return;
    }
  } catch (e) {
    cb();
    return;
  }

  // If we are here, it is not loaded. Set things up so we check   the status again in 100 milliseconds
  window.setTimeout(() => checkIframeLoaded(id, cb), 100);
};

export const getHeadingsForTransactionsTable = (headings, activeNavigation) => {
  if (
    getItemFromLocalStorage('eposStatus') === APPROVED &&
    activeNavigation === ALL
  )
    return headings.filter(item => item.key !== 'customerReferenceId');
  return headings.filter(item => item.key !== 'agentName');
};

export const getCurrencySymbol = currency => {
  // if no currency is passed this is a fallback case where we return - ₹
  if (!currency) return getSymbolFromCurrency('INR');

  const currencySymbol = getSymbolFromCurrency(currency);
  return currencySymbol || currency;
};

export const computeExpiryTime = value => {
  switch (value) {
    case 'IN_12_HRS':
      return moment()
        .add(12, 'hour')
        .format(DEFAULT_DATE_TIME_PAYLOAD_FORMAT);
    case 'IN_1_DAY':
      return moment()
        .add(1, 'day')
        .format(DEFAULT_DATE_TIME_PAYLOAD_FORMAT);
    case 'IN_2_DAYS':
      return moment()
        .add(2, 'days')
        .format(DEFAULT_DATE_TIME_PAYLOAD_FORMAT);
    case 'IN_7_DAYS':
      return moment()
        .add(7, 'days')
        .format(DEFAULT_DATE_TIME_PAYLOAD_FORMAT);
    case 'IN_14_DAYS':
      return moment()
        .add(14, 'days')
        .format(DEFAULT_DATE_TIME_PAYLOAD_FORMAT);
    case 'IN_30_DAYS':
      return moment()
        .add(30, 'days')
        .format(DEFAULT_DATE_TIME_PAYLOAD_FORMAT);
    case 'IN_90_DAYS':
      return moment()
        .add(90, 'days')
        .format(DEFAULT_DATE_TIME_PAYLOAD_FORMAT);
    case 'NO_EXPIRY':
      return null;
    default:
      return moment(value * 1000).format(DEFAULT_DATE_TIME_PAYLOAD_FORMAT);
  }
};

export const hexToRgb = hex => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  // eslint-disable-next-line func-names
  const hexNew = hex.replace(shorthandRegex, function(m, r, g, b) {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexNew);

  if (!result?.length) {
    return null;
  }

  const obj = {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
  return result ? obj : null;
};

export const luminance = (r, g, b) => {
  // eslint-disable-next-line func-names
  const a = [r, g, b].map(function(v) {
    // eslint-disable-next-line no-param-reassign
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
};

export const ratioResult = (color1luminance, color2luminance) => {
  let ratio;
  if (color1luminance > color2luminance) {
    ratio = (color1luminance + 0.05) / (color2luminance + 0.05);
  } else {
    ratio = (color2luminance + 0.05) / (color1luminance + 0.05);
  }
  return ratio > 3 ? 'PASS' : 'FAIL';
};

export const findContrast = themeColor => {
  let textColor;
  const whiteColor = '#FFFFFF';
  const blackColor = '#000000';
  const color1rgb = hexToRgb(themeColor);
  let color2rgb = hexToRgb(whiteColor);
  const color1luminance = luminance(color1rgb.r, color1rgb.g, color1rgb.b);
  let color2luminance = luminance(color2rgb.r, color2rgb.g, color2rgb.b);
  const result = ratioResult(color1luminance, color2luminance);
  if (result === 'PASS') {
    textColor = whiteColor;
  } else {
    color2rgb = hexToRgb(blackColor);
    color2luminance = luminance(color2rgb.r, color2rgb.g, color2rgb.b);
    const newResult = ratioResult(color1luminance, color2luminance);
    if (newResult === 'PASS') {
      textColor = blackColor;
    } else {
      textColor = whiteColor;
    }
  }
  return textColor;
};

export const isEPOSApproved = () =>
  getItemFromLocalStorage('eposStatus') === 'APPROVED';

export const getSummaryAlertBarData = () => {
  const data = ANNOUNCEMENT_STRIP_DATA[SUMMARY];
  return {
    showRechargeAlertBar: !getItemFromLocalStorage(
      data.RECHARGE_ALERT.localStorageKey,
    ),
    showHolidaysAlertBar: !getItemFromLocalStorage(
      data.HOLIDAYS_ALERT.localStorageKey,
    ),
    // showTokenValutAlertBar: !getItemFromLocalStorage(
    //   data.TOKEN_VAULT_ALERT.localStorageKey,
    // ),
    // showGSTSurveyAlertBar: !getItemFromLocalStorage(
    //   data.GST_SURVEY_ALERT.localStorageKey,
    // ),
    // showAdvancesAlertBar: !getItemFromLocalStorage(
    //   data.ADVANCES_ALERT.localStorageKey,
    // ),
    showSoftPOSAlertBar: !getItemFromLocalStorage(
      data.SOFT_POS_ALERT.localStorageKey,
    ),
    // showOnDemandSettlementsAlertsBar: !getItemFromLocalStorage(
    //   data.ON_DEMAND_SETTLEMENTS_ALERT.localStorageKey,
    // ),
    showHoliOfferAlertBar: !getItemFromLocalStorage(
      data.HOLI_OFFER_ALERT.localStorageKey,
    ),
  };
};

export function getUTMObject() {
  const utmCookieValue = getCookie(UTM_CAMPAIGN_URL);
  const utmObject = {};
  Object.keys(UTM_PARAMS).forEach(key => {
    const value = getParam(utmCookieValue, key);
    if (value) utmObject[UTM_PARAMS[key]] = value;
  });
  return utmObject;
}

export const getBrowserData = () => Bowser.parse(window.navigator.userAgent);

export const getStringByteSize = str => new Blob([str]).size;

export const getClassByCharacters = characters => {
  const hasAlphabet = /[a-zA-Z]/.test(characters);
  const hasNumber = /[0-9]/.test(characters);
  const hasSymbol = /[@#$%^&+.=]/.test(characters);

  return {
    character:
      characters.length >= 8 && characters.length <= 20
        ? 'text-success'
        : 'text-dark',
    alphabet: hasAlphabet ? 'text-success' : 'text-dark',
    number: hasNumber ? 'text-success' : 'text-dark',
    symbol: hasSymbol ? 'text-success' : 'text-dark',
  };
};

export const closeGoogleOneTap = () => {
  const googleOneTapNode = document.getElementById(
    'credential_picker_container',
  );
  const oneTapIframeNode = document.getElementById('credential_picker_iframe');
  if (googleOneTapNode) googleOneTapNode.style.display = 'none';
  if (oneTapIframeNode) oneTapIframeNode.style.display = 'none';
};

import { sentryCaptureException as captureSentryException } from './sentry/capture';

export const sentryCaptureException = captureSentryException;

export const generateReCaptcha = cb => {
  if (window.grecaptcha) {
    // eslint-disable-next-line func-names
    window.grecaptcha.ready(function() {
      const optWidgetId = window.grecaptcha.render('recaptcha', {
        sitekey: process.env.GOOGLE_RECAPTCHA_SITE_KEY,
        size: 'invisible',
        callback: token => cb(token, optWidgetId),
      });
      window.grecaptcha.execute(optWidgetId);
    });
  } else {
    cb(' ');
  }
};

export const resetReCaptcha = optWidgetId => {
  if (window.grecaptcha) window.grecaptcha.reset(optWidgetId);
};

/**
 * This method of generating recaptcha overcomes below issues:
 * 1. It resets captcha on its own, so no need to explicitly call `resetRecaptcha`
 * 2. It does not bind the callback function added to submit button, hence would not run into
 *    issues such as input not considering the updated values if submit button was clicked before
 * 3. It does not need any explicit `id` defined on Form element, it can take care of that on its own
 *
 * @param {{onSuccess: (captchaResponse:string) => void, onError: () => void}} param0
 */
export const generateReCAPTCHA = ({ onSuccess, onError }) => {
  if (window.grecaptcha) {
    // eslint-disable-next-line func-names
    window.grecaptcha.ready(function() {
      // create a div with unique id to render reCAPTCHA
      const div = document.createElement('div');
      const id = `recaptcha${Date.now()}`;
      div.setAttribute('id', id);
      document.querySelector('#app').appendChild(div);

      const optWidgetId = window.grecaptcha.render(id, {
        sitekey: process.env.GOOGLE_RECAPTCHA_SITE_KEY,
        size: 'invisible',
        callback: captchaResponse => {
          // reset captcha widget and then remove the div on which reCAPTCHA had rendered
          window.grecaptcha.reset(optWidgetId);
          document.querySelector('#app').removeChild(div);
          onSuccess(captchaResponse);
        },
      });
      window.grecaptcha.execute(optWidgetId);
    });
  } else {
    onError();
  }
};

export const addCurrencySymbol = amount =>
  amount || +amount === 0 ? `${CURRENCY_SYMBOL.INR} ${amount}` : '';

export const captureBlur = (e, data, fn, cb) => {
  if (e.target !== document.activeElement) {
    if (cb) cb();
    fn(e, data);
  }
};

export const findCampaignName = checkListName =>
  FAQ_CAMPAIGNS_CHECKLIST_NAME_MAPPING[checkListName];

export const scrollToTop = query => {
  const element = window.document ? document.querySelector(query) : '';
  if (element && typeof element.scrollTo === 'function')
    element.scrollTo({ top: 0, behaviour: 'auto' });
};

export const getThumbnailImage = link => {
  let id;
  if (link.includes('embed/')) [, id] = link.split('embed/');
  else [, id] = link.split('watch?v=');

  [id] = id.split('?') || id.split('&');

  return `http://img.youtube.com/vi/${id}/0.jpg`;
};

export const getDeviceId = async () => {
  let deviceId = getItemFromLocalStorage('deviceId');
  if (!deviceId) {
    deviceId = await Device.getUniqueId();
    setItemsInLocalStorage({
      deviceId,
    });
  }
  return deviceId;
};

export const getDeviceIdSource = () => {
  let deviceIdSource = getItemFromLocalStorage('deviceIdSource');
  if (!deviceIdSource) {
    deviceIdSource = Device.getDeviceIdSource();
    setItemsInLocalStorage({
      deviceIdSource,
    });
  }
  return deviceIdSource;
};

const getPartnerReqUrlAndToken = () => {
  const env = getEnv();
  const isTestEnv = env === TEST;

  const baseUrl = isTestEnv
    ? process.env.PARTNER_DASHBOARD_TEST_API_URL
    : process.env.PARTNER_DASHBOARD_API_URL;
  const token = LocalStorage.getItemFromLocalStorage(
    isTestEnv ? 'gammaToken' : 'token',
  );

  return { baseUrl, token };
};

const getMerchantReqUrlAndToken = (
  envCall,
  useBasePath,
  isModuleFederated,
  customBasePath,
  isImportMerchant,
  isKong,
  isSubs,
) => {
  let API_URL = useBasePath ? process.env.API_BASE_PATH : process.env.API_URL;
  let TEST_API_URL = useBasePath
    ? process.env.TEST_API_BASE_PATH
    : process.env.TEST_API_URL;

  // Override base paths if customBasePath is passed
  if (customBasePath === CUSTOM_BASE_PATH_TYPES.RISKSHIELD_BASE_PATH) {
    API_URL = process.env.RISK_API_BASE_PATH;
    TEST_API_URL = process.env.TEST_RISK_API_BASE_PATH;
  } else if (
    customBasePath === CUSTOM_BASE_PATH_TYPES.SUBSCRIPTIONS_DASHBOARD_BASE_PATH
  ) {
    API_URL = process.env.SUBSCRIPTION_DASHBOARD_API_URL;
    TEST_API_URL = process.env.SUBSCRIPTION_DASHBOARD_TEST_API_URL;
  } else if (customBasePath === CUSTOM_BASE_PATH_TYPES.BBPS_COU_BASE_PATH) {
    API_URL = process.env.BBPS_COU_API_BASE_PATH || process.env.API_BASE_PATH;
    TEST_API_URL = process.env.TEST_BBPS_COU_API_BASE_PATH || process.env.TEST_API_BASE_PATH;
  }

  if (isImportMerchant) {
    API_URL = process.env.IMPORT_API_BASE_PATH;
    TEST_API_URL = process.env.TEST_IMPORT_API_BASE_PATH;
  }

  if (isKong) {
    API_URL = process.env.KONG_BASE_URL;
    TEST_API_URL = process.env.TEST_KONG_BASE_URL;
  }

  if (isSubs) {
    API_URL = process.env.SUBS_API_BASE_PATH;
    TEST_API_URL = process.env.TEST_SUBS_API_BASE_PATH;
  }

  const env = getEnv();

  const isTestEnv = env === TEST;

  let baseUrl = isTestEnv ? TEST_API_URL : API_URL;
  // Read token from cfToken key in LocalStorage
  const cfTokenVal = getItemFromLocalStorage('cfToken');
  const cfToken = isValidJSON(cfTokenVal)
    ? JSON.parse(cfTokenVal)?.[env || 'prod']?.token
    : null;

  let token = isModuleFederated
    ? cfToken
    : getItemFromLocalStorage(isTestEnv ? 'gammaToken' : 'merchantToken');

  if (envCall) {
    baseUrl = envCall === PROD ? API_URL : TEST_API_URL;
    token =
      envCall === PROD
        ? getItemFromLocalStorage('merchantToken')
        : getItemFromLocalStorage('gammaToken');
  }

  return {
    baseUrl,
    token,
  };
};

export function getRequestUrlAndToken(
  envCall,
  useBasePath,
  isModuleFederated,
  customBasePath = '',
  isImportMerchant,
  isKong = false,
  isSubs = false,
) {
  if (isPartnerDashboard()) return getPartnerReqUrlAndToken();
  return getMerchantReqUrlAndToken(
    envCall,
    useBasePath,
    isModuleFederated,
    customBasePath,
    isImportMerchant,
    isKong,
    isSubs,
  );
}

export const getRequestHeaders = async options => {
  let headers = {
    ...DEFAULT_SECURITY_HEADERS,
    'API-VERSION': process.env.API_VERSION,
    'Device-Id': await getDeviceId(),
    'Device-Id-Source': getDeviceIdSource(),
    'source-type': 'MERCHANT_DASHBOARD',
  };
  if (options.headers) {
    headers = { ...headers, ...options.headers };
  } else if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

export const getFAQClickedPage = pathOfPage => {
  const splittedPathname = pathOfPage
    .split('/')
    .slice(2)
    .join('-');
  return splittedPathname;
};

export const isFAQPrimePresent = handleFailureCase => {
  const helpCTA = document.querySelector('#faq-bot-button');
  if (!helpCTA) handleFailureCase();
};

export const getHtmlCode = (url, data) => `<form>
  <a href="${url}${data.short_code}" target="_parent">
    <div class="button-container" style="background: #${
      data.background_colour
    }">
      <div>
        <img src="${data.button_logo}" alt="logo" class="logo-container">
      </div>
      <div class="text-container">
        <div style="font-family: ${data.button_font_style}; color: #${
  data.button_text_colour
}; margin-bottom: 5px; font-size: 14px;">
          ${data.button_text}
        </div>
        <div style="font-family: ${data.button_font_style}; color: #${
  data.button_text_colour
}; font-size: 10px;">
            <span>Powered By Cashfree</span>
            <img src="https://cashfreelogo.cashfree.com/assets_images/cfbrand/cf_logo.svg" alt="logo" class="seconday-logo-container">
        </div>
      </div>
    </div>
  </a>
 <style>
  .button-container{
      border: 1px solid black;
      border-radius: 15px;
      display: flex;
      padding: 10px;
      width: fit-content;
      cursor: pointer;
  }
  .text-container{
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-left: 10px;
      justify-content: center;
      margin-right: 10px;
  }
  .logo-container{
      width: 40px;
      height: 40px;
  }
  .seconday-logo-container{
    width: 16px;
    height: 16px;
    vertical-align: middle;
  }
  a{
    text-decoration:none;
  }
</style>
</form>`;

export const timeParser = inputTime => {
  const formattedTime = moment(inputTime, 'DD MMM YYYY, hh:mm A').format(
    'YYYY-MM-DD HH:mm:ss',
  );
  return encodeURIComponent(formattedTime);
};

export const isJson = item => {
  let value = typeof item !== 'string' ? JSON.stringify(item) : item;
  try {
    value = JSON.parse(value);
  } catch (e) {
    return false;
  }
  return typeof value === 'object' && value !== null;
};

export const redirectToCommonAuthLogin = () => {
  window.location.href = '/auth/login';
};

export const redirectToOnboarding = () => {
  window.location.href = 'https://cashfree.app.link/ZqO8OZvd6Ob';
};

export const isLocalStorageValueValid = value => {
  // if key is present but value is not then localstorage would return stringified "null" or "undefined"
  // which would be considered as valid string, but it shouldn't
  if (['null', 'undefined'].includes(value)) return false;

  // if key/value both are not present then localStorage would return actual null/undefined
  // which can be evaluated as a boolean
  if (!value) return false;

  return true;
};

export const titleSnakeCase = str =>
  startCase(toLower(str)).replace(/\s/g, '_');

export function isNumberUptoTwoDecimalPlaces(value) {
  const regex = /^\d+(\.\d{1,2})?$/;
  return regex.test(value);
}

export const parsePhoneNumberCSV = (text, filters) => {
  const lines = text.split('\n');
  const result = new Map();
  const headers = lines[0]
    .split(',')
    .map(header => header.trim().replace(/[^a-zA-Z0-9 ]/g, ''));

  headers.forEach(header => {
    if (!filters.headers.includes(header)) {
      throw new Error(filters.errorMessage);
    }
  });

  for (let i = 1; i < lines.length; i += 1) {
    const currentline = lines[i].split(',');
    const phoneNumber = currentline[0]?.trim();

    if (phoneNumber && /^\d+$/.test(phoneNumber)) {
      if (phoneNumber.length !== MAX_PHONE_NUMBER_LENGTH) {
        throw new Error(
          `Phone numbers must be exactly ${MAX_PHONE_NUMBER_LENGTH} digits long.`,
        );
      }

      headers.forEach((header, j) => {
        const trimmedValue = currentline[j]?.trim();
        if (trimmedValue) {
          const values = result.get(header) || [];
          result.set(header, [...values, trimmedValue]);
        }
      });
    }
  }

  const phoneNumbers = Object.fromEntries(result.entries());
  const firstKey = Object.keys(phoneNumbers)[0];
  const phoneNumbersSet = new Set(phoneNumbers[firstKey]);

  if (phoneNumbersSet.size !== phoneNumbers[firstKey]?.length) {
    throw new Error(`Please remove duplicate phone numbers`);
  }

  return phoneNumbers;
};

export const parsePincodeCSV = (text, filters) => {
  const lines = text.split('\n');
  const result = new Map();
  const headers = lines[0]
    .split(',')
    .map(header => header.trim().replace(/[^a-zA-Z0-9 ]/g, ''));

  headers.forEach(header => {
    if (!filters.headers.includes(header)) {
      throw new Error(filters.errorMessage);
    }
  });

  for (let i = 1; i < lines.length; i += 1) {
    const currentline = lines[i].split(',');
    const pincode = currentline[0]?.trim();

    if (pincode && /^\d{6}$/.test(pincode)) {
      headers.forEach((header, j) => {
        const trimmedValue = currentline[j]?.trim();
        if (trimmedValue) {
          const values = result.get(header) || [];
          result.set(header, [...values, trimmedValue]);
        }
      });
    } else if (pincode) {
      throw new Error('Pincodes must be 6-digit numeric values.');
    }
  }

  const pincodes = Object.fromEntries(result.entries());
  const firstKey = Object.keys(pincodes)[0];
  const pincodeSet = new Set(pincodes[firstKey]);

  if (pincodeSet.size !== pincodes[firstKey]?.length) {
    throw new Error('Please remove duplicate pincodes.');
  }

  return pincodes;
};

export const parseProductCSV = (text, filters) => {
  const lines = text.split('\n');
  const result = new Map();
  const headers = lines[0]
    .split(',')
    .map(header => header.trim().replace(/[^a-zA-Z0-9 ]/g, ''));

  headers.forEach(header => {
    if (!filters.headers.includes(header)) {
      throw new Error(filters.errorMessage);
    }
  });

  for (let i = 1; i < lines.length; i += 1) {
    const currentline = lines[i].split(',');
    headers.forEach((header, j) => {
      const trimmedValue = currentline[j]?.trim();
      if (trimmedValue) {
        const values = result.get(header) || [];
        result.set(header, [...values, trimmedValue]);
      }
    });
  }

  const productIds = Object.fromEntries(result.entries());
  const firstKey = Object.keys(productIds)[0];
  const productIdsSet = new Set(productIds[firstKey]);

  if (productIdsSet.size !== productIds[firstKey]?.length) {
    throw new Error('Please remove duplicate products ids.');
  }

  return productIds;
};

export const isFlowwiseDashboard = () => {
  const { pathname } = window.location;
  const envObj = process.env;
  return pathname.includes(envObj.FLOWWISE_BASE_PATH);
};

export const getFlowwiseBasePath = (showInsightsAI = false) => {
  const envObj = process.env;
  return isFlowwiseDashboard() || showInsightsAI
    ? envObj.FLOWWISE_BASE_PATH
    : envObj.ROUTER_BASE_PATH;
};

export const getFlowwiseDefaultMenuPath = routerStatus =>
  routerStatus === APPROVED ? '/gateways' : '/flowwise';

export const getPGDashboardBasePath = () => {
  const envObj = process.env;
  return isFlowwiseDashboard() ? envObj.FLOWWISE_BASE_PATH : 'pg';
};

export function isMerchantEligibleForConfigV2(
  configV2StartDate = FLOWWISE_CONFIGURATION_V2_START_DATE,
) {
  const mIDCreatedAt = getItemFromLocalStorage('mIDCreatedAt');

  if (!mIDCreatedAt) return false;

  const midCreatedDate = moment(mIDCreatedAt, 'DD-MM-YYYY HH:mm:ss');
  const routerConfigV2StartDate = moment(configV2StartDate);

  return midCreatedDate.isAfter(routerConfigV2StartDate);
}

export const getFormattedValues = (data, item) => {
  if (item === SR_CONSTANTS.SUMMARY) {
    const summary = data?.summary || {};
    const formattedSummary = Object.keys(summary).reduce((acc, key) => {
      const readableKey = PAYMENT_MODE_FORMATTED_TEXT[key] || key;
      acc[readableKey] = summary[key];
      return acc;
    }, {});

    return {
      ...data,
      summary: formattedSummary,
    };
  }
  if (item === SR_CONSTANTS.SR_TAB) {
    const updatedData = {
      ...data,
      data:
        data.data?.map(dataPoint => ({
          ...dataPoint,
          category:
            PAYMENT_MODE_FORMATTED_TEXT[dataPoint.category] ||
            dataPoint.category,
        })) || [],
    };
    return updatedData;
  }
  if (item === SR_CONSTANTS.TOP_ERROR_REASONS) {
    const sortedData = data.sort((a, b) => b.errorCount - a.errorCount);

    const updatedData =
      sortedData?.map(({ errorCount, ...rest }) => ({
        ...rest,
        errorCount: formatToIndianNumber(errorCount),
      })) || [];
    return updatedData;
  }
};

export const formatToIndianNumber = num => {
  if (num == null) {
    return '';
  }
  return num.toString().replace(/(\d)(?=(\d\d)+\d$)/g, '$1,');
};

export const getReferrerUrl = () => {
  const referrerUrl = sessionStorage.getItem('referrerUrl');
  /**
   * referrerUrl should only redirect to URLs within same domain. We've many whitelisted domains, e.g., icicimerchant.cashfree.com
   * hence one domain should not redirect to other domain even if user manually modifies the sessionStorage
   */
  const isReferrerURLBelongsToSameDomain = String(referrerUrl).startsWith(
    window.location.origin,
  );
  if (isReferrerURLBelongsToSameDomain) {
    return referrerUrl;
  }
};

export const clearSessionStorage = () => sessionStorage.clear();

export const getEnv = () => {
  const query = QueryParam.getQueryParams();
  const env = query.get('env');

  // Check query params first
  if ([TEST, PROD].includes(env)) return env;

  return LocalStorage.getItemFromLocalStorage('env') || PROD;
};

export const isPartnerDashboard = () =>
  LocalStorage.getItemFromLocalStorage('isPartnerDashboard') === 'true';

export const isLoggedInAsPartner = () => LocalStorage.getItemFromLocalStorage('loggedInAsPartner') === 'true';

export const isValidJSON = str => {
  if (['null', 'undefined'].includes(str)) return false;

  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

export const formatLargeNumber = input => {
  if (!input) return '0.00';

  const stringValue = typeof input === 'number' ? input.toString() : input;

  const number = parseFloat(stringValue.replace(/,/g, ''));

  if (number < 1000) return number.toFixed(2);
  if (number < 1000000) return `${(number / 1000).toFixed(2)}K`;
  if (number < 1000000000) return `${(number / 1000000).toFixed(2)}Mn`;
  return `${(number / 1000000000).toFixed(2)}Bn`;
};

export const convertToTimezone = (timeStamp, timeZone, dateAndTimeFormat) => {
  if (!timeStamp) {
    return;
  }
  const timezoneOffsets = {
    EST: -5, // Eastern Standard Time (UTC -5)
    PST: -8, // Pacific Standard Time (UTC -8)
    IST: +5.5, // Indian Standard Time (UTC +5:30)
    GMT: 0, // Greenwich Mean Time (UTC 0)
    CET: +1, // Central European Time (UTC +1)
  };

  if (timezoneOffsets[timeZone] === undefined) {
    const offset = timezoneOffsets.IST;

    return moment(timeStamp)
      .utc()
      .utcOffset(offset * 60)
      .format(dateAndTimeFormat);
  }

  // Get the UTC offset for the given timeZone
  const offset = timezoneOffsets[timeZone];

  return moment(timeStamp)
    .utc()
    .utcOffset(offset * 60)
    .format(dateAndTimeFormat);
};

export const updateHeaderWithTimezone = (columns, timezone, accessorKey) =>
  columns.map(column =>
    column.accessorKey === accessorKey
      ? { ...column, header: `${column.header} (${timezone})` }
      : column,
  );

export const getDeviceType = () => {
  const deviceSource = sessionStorage.getItem('deviceSource');
  const isValidDeviceType =
    deviceSource && Object.values(DEVICE_TYPES).includes(deviceSource);

  if (isValidDeviceType) return deviceSource;

  const osName = getBrowserData().os.name;
  switch (osName) {
    case ANDROID:
      return DEVICE_TYPES.ANDROID_WEB;
    case IOS:
      return DEVICE_TYPES.IOS_WEB;
    default:
      return DEVICE_TYPES.WEB;
  }
};

export const formatCurrency = (
  amount,
  locale = 'en-IN',
  currency = 'INR',
  digits = { minimumFractionDigits: 2, maximumFractionDigits: 2 },
) => {
  // eslint-disable-next-line no-restricted-globals
  if (isNaN(amount)) {
    return ''; // Return an empty string if the amount is not a valid number
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    ...digits,
  })
    .format(amount)
    ?.replace(CURRENCY_SYMBOL[currency], `${CURRENCY_SYMBOL[currency]} `);
};

export const formattedDate = inputDate =>
  moment(inputDate).format('DD MMM YYYY, hh:mm A');

export const getPreviousTimeRange = dateFilters => {
  const { displayText, range } = dateFilters;

  switch (displayText) {
    case LAST_1_HOUR: {
      return {
        startTime: moment(range[0])
          .subtract(1, 'hour')
          .toDate(),
        endTime: moment(range[0]).toDate(),
        text: 'to previous hour',
      };
    }
    case LAST_3_HOUR: {
      return {
        startTime: moment(range[0])
          .subtract(3, 'hour')
          .toDate(),
        endTime: moment(range[0]).toDate(),
        text: 'to previous 3 hours',
      };
    }
    case LAST_6_HOUR: {
      return {
        startTime: moment(range[0])
          .subtract(6, 'hour')
          .toDate(),
        endTime: moment(range[0]).toDate(),
        text: 'to previous 6 hours',
      };
    }
    case TODAY: {
      return {
        startTime: moment(range[0])
          .subtract(1, 'day')
          .toDate(),
        endTime: moment()
          .subtract(1, 'day')
          .toDate(),
        text: `to yesterday (12:00 AM to ${moment().format('hh:mm A')})`,
      };
    }
    case YESTERDAY: {
      return {
        startTime: moment(range[0])
          .subtract(1, 'day')
          .toDate(),
        endTime: moment(range[0])
          .subtract(1, 'day')
          .endOf('day')
          .toDate(),
        text: 'to previous day',
      };
    }
    case THIS_WEEK: {
      const dayOfWeek = moment().weekday();

      const startTime = moment(range[0])
        .subtract(7, 'days')
        .startOf('day')
        .toDate();

      const endTime = moment()
        .subtract(7, 'days')
        .weekday(dayOfWeek)
        .toDate();

      return {
        startTime,
        endTime,
        text: `to last week (${moment(startTime).format(
          'Do MMMM hh:mm A',
        )} - ${moment(endTime).format('Do MMMM hh:mm A')})`,
      };
    }
    case LAST_WEEK: {
      return {
        startTime: moment(range[0])
          .subtract(7, 'days')
          .startOf('week')
          .toDate(),
        endTime: moment(range[0])
          .subtract(7, 'days')
          .endOf('week')
          .toDate(),
        text: 'to previous week',
      };
    }
    case THIS_MONTH: {
      const today = moment().date();
      const daysInPrevMonth = moment()
        .subtract(1, 'month')
        .daysInMonth();

      const startTime = moment()
        .subtract(1, 'month')
        .startOf('month')
        .toDate();

      const endTime = moment()
        .subtract(1, 'month')
        .date(today < daysInPrevMonth ? today : daysInPrevMonth)
        .toDate();

      return {
        startTime,
        endTime,
        text: `to last month (${moment(startTime).format(
          'Do MMMM hh:mm A',
        )} - ${moment(endTime).format('Do MMMM hh:mm A')})`,
      };
    }
    case LAST_MONTH: {
      return {
        startTime: moment(range[0])
          .subtract(1, 'month')
          .startOf('month')
          .toDate(),
        endTime: moment(range[0])
          .subtract(1, 'month')
          .endOf('month')
          .toDate(),
        text: 'to previous month',
      };
    }
    default: {
      const diff = moment(range[1]).diff(moment(range[0]), 'days');
      const endTime = moment(range[0])
        .subtract(1, 'day')
        .endOf('day');
      const startTime = moment(endTime).subtract(diff, 'days');
      return {
        startTime,
        endTime,
        text: `to (${moment(startTime).format('Do MMMM hh:mm A')} - ${moment(
          endTime,
        ).format('Do MMMM hh:mm A')})`,
      };
    }
  }
};

export const isAmountValue = val => REGEXES.AMOUNT.test(val);

export const isColorDarkOrLight = (color, alpha) => {
  // Check if color is in hexadecimal format, if not, return null
  if (!/^#[0-9A-F]{6}$/i.test(color)) {
    return null;
  }

  // Convert hex color to RGB
  const r = parseInt(color.substring(1, 3), 16);
  const g = parseInt(color.substring(3, 5), 16);
  const b = parseInt(color.substring(5, 7), 16);

  // Apply alpha if provided (blending with white background as default)
  const effectiveAlpha =
    typeof alpha === 'number' ? Math.min(Math.max(alpha, 0), 1) : 1;

  // If alpha is less than 1, blend with white background (255,255,255)
  const blendedR = r * effectiveAlpha + 255 * (1 - effectiveAlpha);
  const blendedG = g * effectiveAlpha + 255 * (1 - effectiveAlpha);
  const blendedB = b * effectiveAlpha + 255 * (1 - effectiveAlpha);

  // Calculate luminance using the formula with the alpha-adjusted RGB values
  const luminanceVal =
    (0.299 * blendedR + 0.587 * blendedG + 0.114 * blendedB) / 255;

  // Return 'dark' if luminance is less than or equal to 0.5, otherwise 'light'
  return luminanceVal <= 0.5 ? 'dark' : 'light';
};

// Generate financial years dynamically: from FY 2015-16 up to the current active FY.
// Indian FY runs April 1 – March 31; the current FY starts in April of the current
// calendar year if today is on/after April 1, otherwise it started last year.
export const getAnnualYears = () => {
  const today = new Date();
  // months are 0-indexed; 3 = April
  const currentFYStartYear =
    today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  const baseYear = 2015;
  const count = currentFYStartYear - baseYear + 1;
  return Array.from({ length: count }, (_, i) => {
    const year = baseYear + i;
    return {
      displayText: `FY ${year}-${(year + 1).toString().slice(-2)}`,
      range: [new Date(`${year}-04-01`), new Date(`${year + 1}-03-31`)],
    };
  }).reverse();
};

export const setNestedValue = (obj, path, value) => {
  const keys = path.split('.');
  let current = obj;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      current[key] = value;
    } else {
      current[key] = current[key] || {};
      current = current[key];
    }
  });
};

export function normalizeUnicodeText(str) {
  return str.replace(REGEXES.UNICODE_WHITESPACE_CLEANER, '');
}

/**
 * Get current timestamp in ISO format
 */
export const getTimestamp = () => new Date().toISOString();

export const trimStringNullSafe = str => {
  if (str === null || str === undefined) return '';
  return str.trim();
};

export const SUBSCRIPTION_STATUS_COLOR_MAP = {
  ACTIVE: {
    text: 'Active',
    textColor: '#00A331',
    backgroundColor: '#D9F1E0',
  },
  INITIALIZED: {
    text: 'Initialized',
    textColor: '#0061AB',
    backgroundColor: '#D9E7F2',
  },
  ON_HOLD: {
    text: 'On Hold',
    textColor: '#D68309',
    backgroundColor: '#F9ECDA',
  },
  CANCELLED: {
    text: 'Cancelled',
    textColor: '#CB2C2C',
    backgroundColor: '#F7DFDF',
  },
  CUSTOMER_CANCELLED: {
    text: 'Customer Cancelled',
    textColor: '#CB2C2C',
    backgroundColor: '#F7DFDF',
  },
  COMPLETED: {
    text: 'Completed',
    textColor: '#0061AB',
    backgroundColor: '#D9E7F2',
  },
  BANK_APPROVAL_PENDING: {
    text: 'Bank Approval Pending',
    textColor: '#D68309',
    backgroundColor: '#F9ECDA',
  },
  FAILED: {
    text: 'Failed',
    textColor: '#CB2C2C',
    backgroundColor: '#F7DFDF',
  },
  LINK_EXPIRED: {
    text: 'Link Expired',
    textColor: '#CB2C2C',
    backgroundColor: '#F7DFDF',
  },
  EXPIRED: {
    text: 'Expired',
    textColor: '#CB2C2C',
    backgroundColor: '#F7DFDF',
  },
  CARD_EXPIRED: {
    text: 'Card Expired',
    textColor: '#CB2C2C',
    backgroundColor: '#F7DFDF',
  },
  PAUSED: {
    text: 'Paused',
    textColor: '#D68309',
    backgroundColor: '#F9ECDA',
  },
  CUSTOMER_PAUSED: {
    text: 'Customer Paused',
    textColor: '#D68309',
    backgroundColor: '#F9ECDA',
  },
  PENDING: {
    text: 'Pending',
    textColor: '#D68309',
    backgroundColor: '#F9ECDA',
  },
};